import {
  printInShortFormatOrAsRelativeDate,
  printInShortDateFormat
} from '../libs/dateTimePrinting';
import {
  deserializeYesplanAPIDate,
  isDateAndTimeAfter,
  isDateAndTimeBefore
} from '../libs/dateTimeUtilities';
import YPTimesheetEntryObject from './YPTimesheetEntryObject';
import {
  OverlapOnBreakWarning,
  ShiftEndsEarlierThanBookingWarning,
  ShiftStartsLaterThanBookingWarning
} from '../libs/timesheetWarnings';
import YPTimesheetLogItemObject from './YPTimesheetLogItemObject';
import YPTimesheetUnavailabilityObject from './YPTimesheetUnavailabilityObject';

export default class YPTimesheetObject {
  static DRAFT_STATUS = 'draft';

  static SUBMITTED_STATUS = 'submitted';

  static PENDING_STATUS = 'pending';

  static APPROVED_STATUS = 'approved';

  static REJECTED_STATUS = 'rejected';

  static FINALIZED_STATUS = 'finalized';

  constructor(dateString, timesheetJSON) {
    this.timesheetJSON = timesheetJSON;
    this.status = timesheetJSON.status;
    this.issueText = timesheetJSON.requestedchanges;
    this.schedules = timesheetJSON.schedules;
    this.date = deserializeYesplanAPIDate(dateString);
    this.entries = this.createTimesheetEntryObjectsFromSchedules();
    this.unavailabilities = this.createTimesheetUnavailabilityObjects();
    this.setTimesheetHistory(timesheetJSON.history);
    this.generateWarningsForEntriesWithActuals();
    this.hasChangedSinceApproval = this.hasEntriesWithWarningsForChangesInFinalState();
  }

  canEdit() {
    return ![
      YPTimesheetObject.APPROVED_STATUS,
      YPTimesheetObject.PENDING_STATUS,
      YPTimesheetObject.SUBMITTED_STATUS,
      YPTimesheetObject.FINALIZED_STATUS
    ].includes(this.status);
  }

  hasChanged() {
    return (
      this.status !== this.timesheetJSON.status ||
      this.issueText !== this.timesheetJSON.requestedchanges ||
      this.entries.some((entry) => entry.hasChanged())
    );
  }

  getDate() {
    return this.date;
  }

  getHistory() {
    return this.history;
  }

  printTimesheetDay(dateFormatSetting, relative = false) {
    if (relative)
      return printInShortFormatOrAsRelativeDate(
        this.date,
        dateFormatSetting,
        true,
        false
      );
    return printInShortDateFormat(this.date, dateFormatSetting, true);
  }

  /* Entry schedule details */
  getEntries() {
    return this.entries;
  }

  hasEntries() {
    return this.entries.length > 0;
  }

  hasSchedules() {
    return this.schedules.length > 0;
  }

  hasUnavailabilities() {
    return this.unavailabilities.length > 0;
  }

  createTimesheetEntryObject(scheduleItem, bookingDetails = null, eventDetails = null) {
    return new YPTimesheetEntryObject(scheduleItem, bookingDetails, eventDetails);
  }

  createTimesheetEntryObjectsFromSchedules() {
    return this.timesheetJSON.schedules.map((schedule) =>
      this.createTimesheetEntryObject(schedule)
    );
  }

  createTimesheetUnavailabilityObjects() {
    return (this.timesheetJSON.unavailabilities || [])
      .map((u) => new YPTimesheetUnavailabilityObject(u))
      .sort((unavailability1, unavailability2) =>
        unavailability1.comparePlannedTimes(unavailability2)
      );
  }

  getUnavailabilities() {
    return this.unavailabilities;
  }

  /**
   * This function sets up the timesheet entries for the detailed view:
   * it adds the necessary info from the linked resourcebookings and events,
   * splits shifts into a 'start' and 'end' part, and sorts them
   */

  extendAndSortEntriesBasedOnBookingDetails(bookingsJSON, eventsJSON) {
    const entryObjects = this.entries.flatMap((entry) => {
      if (entry.isResourceBooking()) {
        const bookingDetails = bookingsJSON.find(
          (bookingJSON) => bookingJSON.id === entry.entryJSON.resourcebooking.id
        );
        const hasEventDetails =
          bookingDetails !== undefined && bookingDetails.event !== undefined;
        const eventDetails = hasEventDetails
          ? eventsJSON.find((eventJSON) => eventJSON.id === bookingDetails.event.id)
          : null;
        entry.setBookingDetails(bookingDetails);
        entry.setEventDetails(eventDetails);
        return entry;
      }
      if (entry.isShift()) {
        entry.setShowOnlyStart();
        const shiftEntryForEnd = this.createTimesheetEntryObject(entry.entryJSON);
        shiftEntryForEnd.setShowOnlyEnd();
        return [entry, shiftEntryForEnd];
      }
      return entry;
    });
    const shifts = entryObjects
      .filter((entry) => entry.isShift())
      .sort((shift1, shift2) => shift1.comparePlannedTimes(shift2));
    const bookingsGroupedByEnclosedShifts = entryObjects
      .filter((entry) => entry.isResourceBooking())
      .reduce((groupedByShift, booking) => {
        const overlappingShifts = shifts.filter((shift) =>
          booking.isEnclosedByEntryWithPlannedTimes(shift)
        );
        if (overlappingShifts.length > 0)
          return { ...groupedByShift, [booking.getId()]: overlappingShifts[0] };
        return groupedByShift;
      }, {});
    this.entries = entryObjects.sort((entry1, entry2) => {
      /* Special case: a shift that is the enclosing shift for a booking that starts earlier */
      if (
        entry1.isShift() &&
        entry1.shouldShowStart() &&
        bookingsGroupedByEnclosedShifts[entry2.getId()]?.getId() === entry1.getId()
      )
        return -1;
      return entry1.comparePlannedTimes(entry2);
    });
  }

  hasEntriesWithWarningsForChangesInFinalState() {
    if (this.isInFinalState()) {
      const finalStateLogItem = this.history.find((entry) => entry.isFinalStateLogItem());
      if (finalStateLogItem)
        return this.history.some((entry) => entry.isAfterLogItem(finalStateLogItem));
    }
    return false;
  }

  getIssueText() {
    return this.issueText || '';
  }

  getStatus() {
    return this.status;
  }

  setStatusToSubmitted() {
    this.status = YPTimesheetObject.SUBMITTED_STATUS;
  }

  setStatusToFinalized() {
    this.status = YPTimesheetObject.FINALIZED_STATUS;
  }

  setStatusToPending() {
    this.status = YPTimesheetObject.PENDING_STATUS;
  }

  isInFinalState() {
    return [
      YPTimesheetObject.APPROVED_STATUS,
      YPTimesheetObject.FINALIZED_STATUS
    ].includes(this.getStatus());
  }

  generateWarningsForBookingsOutsideAShift(bookings) {
    const shifts = this.entries.filter((entry) => entry.isShift());
    shifts.forEach((shift) => {
      bookings
        .filter(
          (booking) =>
            booking.isEnclosedByEntryWithPlannedTimes(shift) &&
            booking.exceedsEnclosingEntryWithTimesheetTimes(shift)
        )
        .forEach((booking) => {
          if (
            isDateAndTimeBefore(booking.getTimesheetStart(), shift.getTimesheetStart()) &&
            shift.shouldShowStart()
          )
            shift.addWarning(new ShiftStartsLaterThanBookingWarning(booking));
          else if (
            isDateAndTimeAfter(booking.getTimesheetEnd(), shift.getTimesheetEnd()) &&
            shift.shouldShowEnd()
          )
            shift.addWarning(new ShiftEndsEarlierThanBookingWarning(booking));
        });
    });
  }

  generateWarningsForBookingsOverlappingBreak(bookings) {
    const breaks = this.entries.filter((entry) => entry.isBreak());
    breaks.forEach((breakEntry) => {
      if (
        bookings.some(
          (booking) =>
            breakEntry.overlapsWithEntry(booking) &&
            !breakEntry.isEntirelyEnclosedByEntryWithPlannedTimes(booking)
        )
      )
        breakEntry.addWarning(new OverlapOnBreakWarning());
    });
  }

  generateWarningsForEntriesWithActuals() {
    const inFinalState = this.isInFinalState();
    const notEditable = !this.canEdit();
    this.entries.forEach((entry) => {
      if (entry.hasActualStatus()) {
        entry.addWarningsOnActualStatus(inFinalState);
      } else {
        if (entry.hasActuals()) {
          entry.addWarningsOnActualTimes(inFinalState, entry.isShift());
        } else {
          entry.addWarningsAboutNoActualTimes(inFinalState);
          entry.addWarningsAboutNoTimesheetTimesOrActualTimesForBooking(notEditable);
        }
      }
    });
  }

  generateWarningsOnEntries() {
    const bookings = this.entries.filter((entry) => entry.isResourceBooking());
    this.generateWarningsForBookingsOverlappingBreak(bookings);
    this.generateWarningsForBookingsOutsideAShift(bookings);
    this.generateWarningsForEntriesWithActuals();
  }

  correctTimesheetStartOrEndTimeOnEntries() {
    this.entries.forEach((entry) => entry.correctTimesheetStartOrEndTime());
  }

  hasWarnings() {
    return this.entries.some((entry) => entry.hasWarnings());
  }

  hasBlockingWarnings() {
    return this.entries.some((entry) => entry.hasBlockingWarnings());
  }

  resetWarnings() {
    this.entries.forEach((entry) => entry.resetWarnings());
  }

  processTimeChange() {
    this.resetWarnings();
    this.correctTimesheetStartOrEndTimeOnEntries();
    this.generateWarningsOnEntries();
  }

  setTimesheetHistory(historyJSON) {
    this.history = this.deserializeTimesheetHistory(historyJSON);
  }

  setTimesheetTimesForUnEditedEntries() {
    this.entries.forEach((entry) => {
      if (!entry.getTimesheetStart(false)) {
        entry.setTimesheetStart(entry.getPlannedStart());
      }
      if (!entry.getTimesheetEnd(false)) {
        entry.setTimesheetEnd(entry.getPlannedEnd());
      }
    });
  }

  deserializeTimesheetHistory(historyJSON) {
    return historyJSON
      .map((historyItemJSON) => new YPTimesheetLogItemObject(historyItemJSON))
      .sort((logItem1, logItem2) => logItem2.getTimestamp() - logItem1.getTimestamp());
  }

  serialize(includeStatus = true, overriddenStatus = null) {
    const shiftsPerId = this.entries
      .filter((entry) => entry.isShift())
      .reduce((shiftsPerIdAcc, shift) => {
        const id = shift.getId();
        if (shiftsPerIdAcc[id] === undefined) shiftsPerIdAcc[id] = [shift];
        else shiftsPerIdAcc[id] = [...shiftsPerIdAcc[id], shift];
        return shiftsPerIdAcc;
      }, {});
    const shifts = Object.values(shiftsPerId).flatMap((shifts) => {
      if (shifts.length !== 2) throw new Error('Found multiple shifts with the same ID');
      const startTimeShift = shifts.find((shift) => shift.shouldShowStart());
      const endTimeShift = shifts.find((shift) => shift.shouldShowEnd());
      startTimeShift.setTimesheetEnd(endTimeShift.getTimesheetEnd());
      return startTimeShift;
    });
    const schedules = [
      ...shifts,
      ...this.entries.filter((entry) => !entry.isShift())
    ].map((entry) => entry.serialize());
    const serializedTimesheet = {
      requestedchanges: this.getIssueText() || null,
      schedules
    };
    if (includeStatus) {
      const status = overriddenStatus || this.getStatus();
      return { ...serializedTimesheet, status };
    }
    return serializedTimesheet;
  }
}
